/**
 * Shop by Category drawer — Enterprise flow, Dawn-compatible.
 * Hover open, sidebar panel swap, flat-hash flyouts, mobile hash panels.
 */
class HeaderCategoryNav extends HTMLElement {
  constructor() {
    super();
    this.menuSensitivity = Number.parseInt(this.dataset.menuSensitivity || '200', 10);
    this.menuLinkTimeout = null;
    this.activeHashPanel = null;
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
  }

  connectedCallback() {
    this.onHashPanelClick = this.onHashPanelClick.bind(this);
    this.onMobileDrawerClose = this.onMobileDrawerClose.bind(this);
    this.updateCategoryDrawerTop = this.updateCategoryDrawerTop.bind(this);
    this.bindHashPanels();
    this.bindMobileCategoryDrawer();

    this.trigger = this.querySelector('[data-category-trigger]');
    this.drawer = this.querySelector('#HeaderCategoryDrawer');
    this.overlay = this.querySelector('[data-category-overlay]');
    this.sidebarLinks = this.querySelectorAll('.js-sidebar-hover');

    if (!this.trigger || !this.drawer) return;

    this.onTriggerEnter = this.onTriggerEnter.bind(this);
    this.onTriggerLeave = this.onTriggerLeave.bind(this);
    this.onDrawerEnter = this.onDrawerEnter.bind(this);
    this.onDrawerLeave = this.onDrawerLeave.bind(this);
    this.onOverlayClick = this.close.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSidebarHover = this.onSidebarHover.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);

    this.bindDesktop();
    this.initFlatHashFlyouts();
    this.bindSearchInteraction();
    this.mediaQuery.addEventListener('change', this.onMediaChange);

    const firstVisible = this.querySelector('.js-sidebar-hover.is-visible');
    if (firstVisible) this.setSidebarHeight(firstVisible);
  }

  disconnectedCallback() {
    this.unbindDesktop();
    this.unbindSearchInteraction();
    this.mediaQuery.removeEventListener('change', this.onMediaChange);
    document.removeEventListener('keyup', this.onKeyUp);
    this.removeEventListener('click', this.onHashPanelClick);
    document.removeEventListener('click', this.onHashPanelClick, true);
    document.removeEventListener('click', this.onMobileDrawerClose, true);
    if (this.categoryDrawerDetails && this.onCategoryDrawerToggle) {
      this.categoryDrawerDetails.removeEventListener('toggle', this.onCategoryDrawerToggle);
      window.removeEventListener('resize', this.onCategoryDrawerToggle);
    }
    window.removeEventListener('resize', this.updateCategoryDrawerTop);
    document.documentElement.style.removeProperty('--category-drawer-top');
    this.restoreCategoryDrawer();
    document.body.classList.remove('mobile-category-drawer-open');
    this.unlockPageScroll();
  }

  onMediaChange() {
    this.unbindDesktop();
    if (this.mediaQuery.matches) {
      this.bindDesktop();
    } else {
      this.close();
    }
  }

  bindDesktop() {
    if (!this.mediaQuery.matches) return;
    this.trigger.addEventListener('mouseenter', this.onTriggerEnter);
    this.trigger.addEventListener('mouseleave', this.onTriggerLeave);
    this.trigger.addEventListener('focus', this.onTriggerEnter);
    this.drawer.addEventListener('mouseenter', this.onDrawerEnter);
    this.drawer.addEventListener('mouseleave', this.onDrawerLeave);
    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClick);
      this.overlay.addEventListener('mouseenter', this.onOverlayEnter);
    }
    document.addEventListener('keyup', this.onKeyUp);
    this.sidebarLinks.forEach((el) => {
      el.addEventListener('mouseenter', this.onSidebarHover);
    });
  }

  unbindDesktop() {
    this.trigger?.removeEventListener('mouseenter', this.onTriggerEnter);
    this.trigger?.removeEventListener('mouseleave', this.onTriggerLeave);
    this.trigger?.removeEventListener('focus', this.onTriggerEnter);
    this.drawer?.removeEventListener('mouseenter', this.onDrawerEnter);
    this.drawer?.removeEventListener('mouseleave', this.onDrawerLeave);
    this.overlay?.removeEventListener('click', this.onOverlayClick);
    this.overlay?.removeEventListener('mouseenter', this.onOverlayEnter);
    document.removeEventListener('keyup', this.onKeyUp);
    this.sidebarLinks.forEach((el) => {
      el.removeEventListener('mouseenter', this.onSidebarHover);
    });
  }

  isRelatedInsideNav(related) {
    if (!related || !(related instanceof Node)) return false;
    // Only the trigger + mega drawer keep the menu open — NOT the dim overlay
    return Boolean(
      this.trigger?.contains(related) ||
        this.drawer?.contains(related) ||
        related === this.trigger ||
        related === this.drawer
    );
  }

  onTriggerEnter() {
    clearTimeout(this.menuLinkTimeout);
    this.menuLinkTimeout = setTimeout(() => this.open(), this.menuSensitivity);
  }

  onTriggerLeave(evt) {
    if (this.isRelatedInsideNav(evt.relatedTarget)) return;
    clearTimeout(this.menuLinkTimeout);
    this.menuLinkTimeout = setTimeout(() => this.close(), this.menuSensitivity);
  }

  onDrawerEnter() {
    clearTimeout(this.menuLinkTimeout);
  }

  onDrawerLeave(evt) {
    if (this.isRelatedInsideNav(evt.relatedTarget)) return;
    clearTimeout(this.menuLinkTimeout);
    this.menuLinkTimeout = setTimeout(() => this.close(), this.menuSensitivity);
  }

  onOverlayEnter = () => {
    // Cursor on the dim backdrop means we've left the menu — close
    clearTimeout(this.menuLinkTimeout);
    this.menuLinkTimeout = setTimeout(() => this.close(), this.menuSensitivity);
  };

  onKeyUp(evt) {
    if (evt.key === 'Escape') this.close();
  }

  bindSearchInteraction() {
    this.searchInput = document.querySelector('#CustomSearch');
    this.onSearchFocus = () => this.close();
    this.onSearchInput = () => {
      if (this.searchInput?.value.trim()) this.close();
    };

    this.searchInput?.addEventListener('focus', this.onSearchFocus);
    this.searchInput?.addEventListener('input', this.onSearchInput);
    document.addEventListener('mousedown', this.onSortInteractCapture, true);
    document.addEventListener('focusin', this.onSortInteractCapture, true);
  }

  onSortInteractCapture = (event) => {
    const sortEl = event.target?.closest?.('select.facet-filters__sort, #SortBy, .facet-filters__field');
    if (!sortEl) return;
    this.close();
  };

  unbindSearchInteraction() {
    this.searchInput?.removeEventListener('focus', this.onSearchFocus);
    this.searchInput?.removeEventListener('input', this.onSearchInput);
    document.removeEventListener('mousedown', this.onSortInteractCapture, true);
    document.removeEventListener('focusin', this.onSortInteractCapture, true);
  }

  isHeaderSearchOpen() {
    const suggestion = document.querySelector('.header-search-box .customSearchSuggestion');
    if (suggestion && window.getComputedStyle(suggestion).display !== 'none') return true;
    return Boolean(document.querySelector('.header-search-box predictive-search[open]'));
  }

  closeHeaderSearch() {
    const suggestion = document.querySelector('.header-search-box .customSearchSuggestion');
    if (suggestion) {
      suggestion.style.display = 'none';
      const list = suggestion.querySelector('ul');
      if (list) list.innerHTML = '';
    }

    const predictive = document.querySelector('.header-search-box predictive-search');
    if (predictive && typeof predictive.close === 'function') {
      predictive.close(true);
    }

    if (this.searchInput && document.activeElement === this.searchInput) {
      this.searchInput.blur();
    }
  }

  onPageScrollLock = (event) => {
    if (!this.classList.contains('is-open')) return;
    // Allow scrolling inside the mega menu panel if it overflows
    if (event.target.closest?.('.header-category-drawer')) return;
    event.preventDefault();
  };

  updateOverlayTop = () => {
    const headerEl =
      this.querySelector('.header') ||
      document.querySelector('.section-header') ||
      this;
    const top = Math.round(headerEl.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--category-overlay-top', `${Math.max(0, top)}px`);
  };

  lockPageScroll() {
    if (this.scrollLocked) return;
    this.scrollLocked = true;
    this.updateOverlayTop();
    this.overlay?.classList.add('is-visible');
    // Wheel/touch lock only — no overflow:hidden (avoids scrollbar layout shift loops)
    document.addEventListener('wheel', this.onPageScrollLock, { passive: false });
    document.addEventListener('touchmove', this.onPageScrollLock, { passive: false });
    window.addEventListener('resize', this.updateOverlayTop);
  }

  unlockPageScroll() {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;
    this.overlay?.classList.remove('is-visible');
    document.removeEventListener('wheel', this.onPageScrollLock);
    document.removeEventListener('touchmove', this.onPageScrollLock);
    window.removeEventListener('resize', this.updateOverlayTop);
    document.documentElement.style.removeProperty('--category-overlay-top');
  }

  closeOpenPageSelects() {
    // Native <select> menus paint above CSS stacking — blur so they can't overlap the mega menu
    document.querySelectorAll('select.facet-filters__sort, #SortBy, select:focus').forEach((el) => {
      if (el instanceof HTMLSelectElement) el.blur();
    });
    if (document.activeElement instanceof HTMLSelectElement) {
      document.activeElement.blur();
    }
  }

  open() {
    if (!this.mediaQuery.matches) return;
    if (this.classList.contains('is-open')) return;
    if (this.isHeaderSearchOpen()) return;

    this.closeHeaderSearch();
    this.closeOpenPageSelects();
    this.classList.add('is-open');
    this.drawer?.removeAttribute('hidden');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.lockPageScroll();
    const firstVisible = this.querySelector('.js-sidebar-hover.is-visible');
    if (firstVisible) this.setSidebarHeight(firstVisible);
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    clearTimeout(this.menuLinkTimeout);
    this.classList.remove('is-open');
    this.drawer?.setAttribute('hidden', '');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.unlockPageScroll();
    this.closeActiveHashPanel();
  }

  onSidebarHover(evt) {
    const listElem = evt.currentTarget;
    const container = listElem.closest('.child-nav');
    if (!container) return;

    const lastVisible = container.querySelector('.is-visible');
    if (lastVisible && lastVisible !== listElem) {
      lastVisible.classList.remove('is-visible');
    }
    listElem.classList.add('is-visible');
    this.setSidebarHeight(listElem);
  }

  setSidebarHeight(listElem) {
    const container = listElem.closest('.child-nav');
    const openPanel = listElem.querySelector('.child-nav__panel');
    if (!container || !openPanel) return;

    requestAnimationFrame(() => {
      let sidebarHeight = 0;
      container.querySelectorAll(':scope > li').forEach((li) => {
        const row = li.querySelector(':scope > .child-nav__row, :scope > .child-nav__item');
        sidebarHeight += row ? row.getBoundingClientRect().height : li.getBoundingClientRect().height;
      });

      const content = openPanel.querySelector('.main-nav__grandchild');
      const contentHeight = content ? content.scrollHeight : 0;
      const height = Math.max(Math.round(sidebarHeight), Math.round(contentHeight), 280);
      container.style.setProperty('--sidebar-height', `${height}px`);
    });
  }

  initFlatHashFlyouts() {
    this.querySelectorAll('.js-nav-flat-hash-flyout').forEach((flyoutItem) => {
      const flyout = flyoutItem.querySelector('.nav-flat-hash__flyout');
      if (!flyout) return;

      const positionFlyout = () => {
        flyout.classList.remove('nav-flat-hash__flyout--inverse');
        if (!this.mediaQuery.matches) return;
        const rect = flyout.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          flyout.classList.add('nav-flat-hash__flyout--inverse');
        }
      };

      flyoutItem.addEventListener('mouseenter', positionFlyout);
      flyoutItem.addEventListener('focusin', positionFlyout);
    });
  }

  bindHashPanels() {
    // Capture on document so clicks still work after drawer is moved to <body>
    document.addEventListener('click', this.onHashPanelClick, true);
  }

  finishCategoryDrawerCloseStart() {
    this._portedCategoryDrawer?.classList.remove('is-drawer-open');
    this.setAnnouncementHidden(false);
    window.removeEventListener('resize', this.updateCategoryDrawerTop);
    document.documentElement.style.removeProperty('--category-drawer-top');
    document.body.classList.remove(
      'mobile-category-drawer-open',
      'overflow-hidden',
      'overflow-hidden-tablet',
      'overflow-hidden-mobile'
    );
  }

  bindMobileCategoryDrawer() {
    this.categoryDrawerDetails = this.querySelector('header-drawer details');
    this.onCategoryDrawerToggle = this.onCategoryDrawerToggle.bind(this);

    // Capture so close (X) works while drawer is portaled to body
    document.addEventListener('click', this.onMobileDrawerClose, true);

    const headerDrawer = this.querySelector('header-drawer');
    if (headerDrawer && !headerDrawer._categoryDrawerClosePatched) {
      const originalClose = headerDrawer.closeMenuDrawer.bind(headerDrawer);
      headerDrawer.closeMenuDrawer = (event, elementToFocus) => {
        if (this.categoryDrawerDetails?.querySelector('.menu-drawer--category')) {
          this.finishCategoryDrawerCloseStart();
        }
        originalClose(event, elementToFocus);
      };
      headerDrawer._categoryDrawerClosePatched = true;
    }

    if (this.categoryDrawerDetails?.querySelector('.menu-drawer--category')) {
      this.categoryDrawerDetails.addEventListener('toggle', this.onCategoryDrawerToggle);
      window.addEventListener('resize', this.onCategoryDrawerToggle);
    }
  }

  setAnnouncementHidden(hidden) {
    document.querySelectorAll('.announcement-bar-section, [id*="announcement-bar"]').forEach((el) => {
      if (hidden) {
        el.setAttribute('data-category-drawer-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
      } else if (el.getAttribute('data-category-drawer-hidden') === 'true') {
        el.removeAttribute('data-category-drawer-hidden');
        el.style.removeProperty('display');
      }
    });
  }

  updateCategoryDrawerTop() {
    // Anchor under the main black header bar only — not secondary nav
    const headerEl =
      document.querySelector('.header-vguard__top') ||
      document.querySelector('.header.header--vguard') ||
      document.querySelector('.section-header') ||
      this;
    const top = Math.round(headerEl.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--category-drawer-top', `${Math.max(0, top)}px`);
  }

  portalCategoryDrawer() {
    const details = this.categoryDrawerDetails;
    const drawer = details?.querySelector('.menu-drawer--category') || this._portedCategoryDrawer;
    if (!drawer || !details?.open) return;

    if (!this._portedCategoryDrawer) {
      this._categoryDrawerHome = drawer.parentElement;
      this._categoryDrawerNext = drawer.nextSibling;
      this._portedCategoryDrawer = drawer;
    }

    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }

    this.setAnnouncementHidden(true);
    this.updateCategoryDrawerTop();
    drawer.classList.add('is-drawer-open');
    document.body.classList.add('mobile-category-drawer-open');

    drawer.scrollTop = 0;
    drawer.querySelectorAll('.menu-drawer__navigation-container, .menu-drawer__inner-container, .menu-drawer__submenu').forEach((el) => {
      el.scrollTop = 0;
    });
  }

  restoreCategoryDrawer() {
    const drawer = this._portedCategoryDrawer;
    if (!drawer) {
      this.setAnnouncementHidden(false);
      document.body.classList.remove('mobile-category-drawer-open');
      return;
    }

    drawer.classList.remove('is-drawer-open');
    document.body.classList.remove('mobile-category-drawer-open');
    this.setAnnouncementHidden(false);

    if (this._categoryDrawerHome && drawer.parentElement === document.body) {
      if (this._categoryDrawerNext && this._categoryDrawerNext.parentElement === this._categoryDrawerHome) {
        this._categoryDrawerHome.insertBefore(drawer, this._categoryDrawerNext);
      } else {
        this._categoryDrawerHome.appendChild(drawer);
      }
    }
  }

  resetCategoryDrawerNavigation() {
    const drawer =
      this._portedCategoryDrawer ||
      this.categoryDrawerDetails?.querySelector('.menu-drawer--category');
    if (!drawer) return;

    this.closeActiveHashPanel();

    drawer.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });

    drawer.querySelectorAll('.submenu-open').forEach((el) => {
      el.classList.remove('submenu-open');
    });

    drawer.querySelectorAll('.nav-flat-hash__panel.is-open').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    drawer.querySelectorAll('.nav-flat-hash__list-wrap[aria-hidden="true"]').forEach((wrap) => {
      wrap.removeAttribute('aria-hidden');
    });
    drawer.querySelectorAll('.js-open-hash-panel[aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    drawer.scrollTop = 0;
    drawer.querySelectorAll('.menu-drawer__navigation-container, .menu-drawer__inner-container, .menu-drawer__submenu').forEach((el) => {
      el.scrollTop = 0;
    });
  }

  onCategoryDrawerToggle() {
    const details = this.categoryDrawerDetails;

    if (details?.open) {
      // Hide announcement immediately, then portal drawer below the main header
      document.body.classList.add('mobile-category-drawer-open');
      this.setAnnouncementHidden(true);
      this.updateCategoryDrawerTop();
      requestAnimationFrame(() => {
        this.updateCategoryDrawerTop();
        this.portalCategoryDrawer();
      });
      window.addEventListener('resize', this.updateCategoryDrawerTop);
      return;
    }

    window.removeEventListener('resize', this.updateCategoryDrawerTop);
    document.documentElement.style.removeProperty('--category-drawer-top');

    // Portaled drawer lives outside <details>, so Dawn's closeMenuDrawer won't reset submenus
    this.resetCategoryDrawerNavigation();

    const drawerSlideOut = this._portedCategoryDrawer;
    drawerSlideOut?.classList.remove('is-drawer-open');
    this.setAnnouncementHidden(false);
    document.body.classList.remove(
      'mobile-category-drawer-open',
      'overflow-hidden',
      'overflow-hidden-tablet',
      'overflow-hidden-mobile'
    );

    const summary = this.categoryDrawerDetails?.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');

    window.setTimeout(() => {
      if (!this.categoryDrawerDetails?.open) {
        this.restoreCategoryDrawer();
      }
    }, 400);
  }

  onMobileDrawerClose(evt) {
    // Category drawer closes via header hamburger/X toggle; keep handler for legacy markup
    const closeBtn = evt.target.closest('.js-menu-drawer-close');
    if (!closeBtn) return;
    if (!closeBtn.closest('.menu-drawer--category')) return;

    evt.preventDefault();
    evt.stopPropagation();
    const headerDrawer = this.querySelector('header-drawer');
    const summary = headerDrawer?.querySelector('summary');
    if (!headerDrawer || !summary) return;

    const portaledDrawer = this._portedCategoryDrawer;
    portaledDrawer?.classList.remove('is-drawer-open');

    this.resetCategoryDrawerNavigation();
    summary.setAttribute('aria-expanded', 'false');
    this.finishCategoryDrawerCloseStart();
    headerDrawer.closeMenuDrawer(evt, summary);
  }

  onHashPanelClick(evt) {
    const openBtn = evt.target.closest('.js-open-hash-panel');
    const closeBtn = evt.target.closest('.js-close-hash-panel');

    if (openBtn) {
      evt.preventDefault();
      const panelId = openBtn.getAttribute('aria-controls');
      const panel = document.getElementById(panelId);
      const container = openBtn.closest('.nav-flat-hash--mobile');
      if (!panel || !container) return;

      this.closeActiveHashPanel();
      container.querySelector('.nav-flat-hash__list-wrap')?.setAttribute('aria-hidden', 'true');
      panel.setAttribute('aria-hidden', 'false');
      panel.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      this.activeHashPanel = { panel, container, trigger: openBtn };
    }

    if (closeBtn) {
      evt.preventDefault();
      this.closeActiveHashPanel();
    }
  }

  closeActiveHashPanel() {
    if (!this.activeHashPanel) return;
    const { panel, container, trigger } = this.activeHashPanel;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    container?.querySelector('.nav-flat-hash__list-wrap')?.removeAttribute('aria-hidden');
    trigger?.setAttribute('aria-expanded', 'false');
    this.activeHashPanel = null;
  }
}

customElements.define('header-category-nav', HeaderCategoryNav);
